import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Activity, Clock, History, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  useEngineer, useUpdateEngineer,
  useEngineerSignals, useEngineerOverrides, useCreateOverride,
  useBaselineHistory,
} from '../../api/engineers';
import { useTeamList } from '../../api/teams';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { DetailPageSkeleton } from '@/components/ui/skeleton';
import ErrorState from '../../components/ui/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '../../lib/dates';

export default function EngineerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();

  const { data: engineer, isLoading, error, refetch } = useEngineer(id!);
  const { data: signals }         = useEngineerSignals(id!);
  const { data: overrides }       = useEngineerOverrides(id!);
  const { data: baselineHistory } = useBaselineHistory(id!);
  const { data: teams }           = useTeamList();
  const updateEngineer = useUpdateEngineer(id!);
  const createOverride = useCreateOverride(id!);

  const [showBaseline, setShowBaseline] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  const baselineForm = useForm<{ baselinePoints: number; baselineCycleDays: number }>();
  const overrideForm = useForm<{ reason: string; expiresAt: string }>();

  if (isLoading)            return <DetailPageSkeleton maxW="3xl" />;
  if (error || !engineer)   return <ErrorState onRetry={refetch} />;

  const handleBaseline = baselineForm.handleSubmit((values) =>
    updateEngineer.mutate(
      { baselinePoints: Number(values.baselinePoints), baselineCycleDays: Number(values.baselineCycleDays) },
      { onSuccess: () => { toast.success('Baselines updated.'); setShowBaseline(false); } },
    ),
  );

  const handleOverride = overrideForm.handleSubmit((values) =>
    createOverride.mutate(
      { reason: values.reason, expiresAt: values.expiresAt || undefined },
      { onSuccess: () => { toast.success('Override granted.'); setShowOverride(false); overrideForm.reset(); } },
    ),
  );

  const signalRows = signals ? [
    { label: 'Load vs baseline', signal: signals.loadVsBaseline },
    { label: 'Concurrent tasks', signal: signals.concurrent },
    { label: 'Stale in-progress', signal: signals.staleInProgress },
  ] : [];

  return (
    <div className="max-w-2xl">
      <PageHeader
        title={engineer.name}
        description={engineer.email}
        actions={
          can('head_of_rd') ? (
            <Button size="sm" variant="secondary" onClick={() => {
              baselineForm.reset({ baselinePoints: engineer.baselinePoints, baselineCycleDays: engineer.baselineCycleDays });
              setShowBaseline((v) => !v);
            }}>
              Edit baselines
            </Button>
          ) : undefined
        }
      />

      {/* Profile */}
      <Card className={cn('mb-5 overflow-hidden border-l-4', engineer.isActive ? 'border-l-emerald-400' : 'border-l-gray-300')}>
        <CardContent className="p-5">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-base font-bold text-primary">
              {engineer.name.split(' ').slice(0, 2).map((n: string) => n[0]).join('').toUpperCase()}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge label={engineer.isActive ? 'active' : 'inactive'} variant={engineer.isActive ? 'green' : 'gray'} />
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize text-muted-foreground">
                {engineer.role.replace(/_/g, ' ')}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Team</p>
              <p className="mt-1 font-medium text-foreground">{teams?.find((t) => t.id === engineer.teamId)?.name ?? engineer.team ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Baseline points</p>
              <p className="mt-1 font-medium text-foreground">{engineer.baselinePoints}</p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Baseline cycle days</p>
              <p className="mt-1 font-medium text-foreground">{engineer.baselineCycleDays}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Baselines edit */}
      {showBaseline && can('head_of_rd') && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <SlidersHorizontal className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Edit baselines</p>
          </div>
          <form onSubmit={handleBaseline}>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Baseline points</Label>
                <Input type="number" min={1} {...baselineForm.register('baselinePoints')} />
              </div>
              <div className="space-y-1.5">
                <Label>Baseline cycle days</Label>
                <Input type="number" min={1} {...baselineForm.register('baselineCycleDays')} />
              </div>
            </div>
            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={updateEngineer.isPending}>Save</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => setShowBaseline(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Overwork signals */}
      {signals && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md',
              signals.isOverworked && !signals.hasActiveOverride ? 'bg-red-50 dark:bg-red-950/40' : 'bg-muted',
            )}>
              <Activity className={cn('h-4 w-4', signals.isOverworked && !signals.hasActiveOverride ? 'text-red-500' : 'text-muted-foreground')} />
            </div>
            <p className="text-sm font-semibold text-foreground">Overwork signals</p>
            <div className="ml-auto flex items-center gap-2">
              {signals.isOverworked && !signals.hasActiveOverride && <Badge label="overworked" variant="red" />}
              {signals.hasActiveOverride && <Badge label="override active" variant="yellow" />}
            </div>
          </div>
          <CardContent className="p-5">
            <div className="space-y-3">
              {signalRows.map(({ label, signal }) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{signal.reason}</span>
                    <Badge label={signal.tripped ? 'tripped' : 'ok'} variant={signal.tripped ? 'red' : 'green'} />
                  </div>
                </div>
              ))}
            </div>
            {signals.isOverworked && (
              <div className="mt-4 border-t border-border pt-4">
                <Button size="sm" variant="secondary" onClick={() => setShowOverride((v) => !v)}>Grant override</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Override form */}
      {showOverride && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
              <ShieldCheck className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">Grant overwork override</p>
          </div>
          <form onSubmit={handleOverride}>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Reason <span className="text-destructive">*</span></Label>
                <Textarea rows={3} {...overrideForm.register('reason', { required: 'Reason is required' })} />
                {overrideForm.formState.errors.reason && (
                  <p className="text-xs text-destructive">{overrideForm.formState.errors.reason.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Expires at <span className="text-muted-foreground">(optional)</span></Label>
                <Input type="datetime-local" {...overrideForm.register('expiresAt')} />
              </div>
            </div>
            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={createOverride.isPending}>Grant</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => setShowOverride(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Override history */}
      {overrides && overrides.length > 0 && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-foreground">Override history</p>
          </div>
          <div className="divide-y divide-border">
            {overrides.map((o) => (
              <div key={o.id} className="px-5 py-4 text-sm">
                <p className="font-medium text-foreground">{o.reason}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Granted {formatDate(o.createdAt)}
                  {o.expiresAt && ` · Expires ${formatDateTime(o.expiresAt)}`}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Baseline calibration history */}
      {can('head_of_rd') && baselineHistory && baselineHistory.length > 0 && (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
              <History className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-foreground">Baseline calibration history</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {baselineHistory.map((h, i) => (
                <TableRow key={i}>
                  <TableCell className="text-muted-foreground">{formatDateTime(h.changedAt)}</TableCell>
                  <TableCell className="text-muted-foreground">{h.fromPoints} pts / {h.fromCycleDays}d</TableCell>
                  <TableCell className="font-medium text-foreground">{h.toPoints} pts / {h.toCycleDays}d</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
